import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, ExternalLink, Play, Share2, Video } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

const PalavraViva = () => {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  const videos = [
    {
      titulo: "O que aconteceu antes da criação do mundo? Jesus explicou #memesgospel",
      link: "https://rumble.com/v6u23mj-o-que-aconteceu-antes-da-criao-do-mundo-jesus-explicou-memesgospel.html",
      thumbnail: "https://sp.rmbl.ws/s8/6/g/h/5/x/gh5xg.OvCc.1.jpg",
      descricao: "Uma reflexão sobre o que existia antes da criação do mundo, segundo as palavras de Jesus."
    },
    {
      titulo: "NOS TRILHOS DO SENHOR COMPLETAO.mp4",
      link: "https://rumble.com/v6sl2ud-nos-trilhos-do-senhor-completao.mp4.html?e9s=src_v1_ucp",
      thumbnail: "https://sp.rmbl.ws/s8/6/S/U/q/i/SUqii.OvCc.jpg",
      descricao: "Uma jornada completa nos caminhos do Senhor."
    },
    {
      titulo: "Tudo entreguei ao senhor",
      link: "https://www.youtube.com/watch?v=9nfiZe2-f0c",
      thumbnail: "https://i.ytimg.com/vi/9nfiZe2-f0c/hqdefault.jpg",
      descricao: "Música de louvor e adoração - Tudo entreguei ao senhor."
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Palavra Viva Online
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Mensagens e reflexões para alimentar sua fé e fortalecer sua caminhada espiritual.
          </p>
        </div>

        <div className="mb-12">
          <Card className="overflow-hidden border border-border">
            <CardHeader className="bg-muted/50">
              <CardTitle className="text-xl">Canal Palavra Viva Online no Rumble</CardTitle>
              <CardDescription>
                Acompanhe nossos vídeos e pregações inspiradoras
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <p className="text-muted-foreground">
                  Nosso canal no Rumble traz conteúdos inspiradores, pregações, músicas gospel e reflexões bíblicas para edificar sua vida espiritual. Inscreva-se e acompanhe nossas atualizações.
                </p>
                <div className="flex flex-wrap gap-4 items-center justify-between">
                  <Button 
                    asChild 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <a 
                      href="https://rumble.com/c/c-7697051" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Video className="mr-2 h-5 w-5" /> Visitar Canal Palavra Viva
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handleCopy("https://rumble.com/c/c-7697051")}
                    className={copied === "https://rumble.com/c/c-7697051" ? "bg-primary/10" : ""}
                  >
                    <Copy size={16} className="mr-1" /> 
                    {copied === "https://rumble.com/c/c-7697051" ? "Copiado!" : "Copiar Link"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Canal de Oração - Salmos</h2>
          <Card className="overflow-hidden border border-border mb-8">
            <CardHeader className="bg-muted/50">
              <CardTitle className="text-xl flex items-center">
                <Video className="mr-2 h-5 w-5 text-primary" /> Canal de Oração
              </CardTitle>
              <CardDescription>Momentos de reflexão e oração através dos Salmos</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <p className="text-muted-foreground">
                  O Canal de Oração traz leituras dos Salmos e momentos de reflexão para fortalecer sua vida de oração. Um espaço para encontrar paz e conexão espiritual através das escrituras.
                </p>
                <div className="flex flex-wrap gap-4 items-center justify-between">
                  <Button 
                    asChild 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <a 
                      href="https://rumble.com/c/c-3001464" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Visitar Canal de Oração
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handleCopy("https://rumble.com/c/c-3001464")}
                    className={copied === "https://rumble.com/c/c-3001464" ? "bg-primary/10" : ""}
                  >
                    <Copy size={16} className="mr-1" /> 
                    {copied === "https://rumble.com/c/c-3001464" ? "Copiado!" : "Copiar Link"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Canal Corte Certo no Rumble</h2>
          <Card className="overflow-hidden border border-border mb-8">
            <CardHeader className="bg-muted/50">
              <CardTitle className="text-xl flex items-center">
                <Video className="mr-2 h-5 w-5 text-primary" /> Corte Certo
              </CardTitle>
              <CardDescription>Os melhores cortes de vídeos e conteúdos selecionados</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <p className="text-muted-foreground">
                  O canal Corte Certo traz os melhores momentos e recortes de pregações, testemunhos e conteúdos inspiradores. Uma forma rápida e prática de acessar mensagens poderosas.
                </p>
                <div className="flex flex-wrap gap-4 items-center justify-between">
                  <Button 
                    asChild 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <a 
                      href="https://rumble.com/c/c-7706553" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Visitar Corte Certo
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handleCopy("https://rumble.com/c/c-7706553")}
                    className={copied === "https://rumble.com/c/c-7706553" ? "bg-primary/10" : ""}
                  >
                    <Copy size={16} className="mr-1" /> 
                    {copied === "https://rumble.com/c/c-7706553" ? "Copiado!" : "Copiar Link"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Canal no YouTube - Trilhos e Trem</h2>
          <Card className="overflow-hidden border border-border mb-8">
            <CardHeader className="bg-muted/50">
              <CardTitle className="text-xl flex items-center">
                <Video className="mr-2 h-5 w-5 text-primary" /> Trilhos e Trem
              </CardTitle>
              <CardDescription>Conteúdo sobre trens, ferrovias e curiosidades</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <p className="text-muted-foreground">
                  O canal Trilhos e Trem traz conteúdos sobre ferrovias, viagens de trem, curiosidades e histórias relacionadas ao mundo ferroviário.
                </p>
                <div className="flex flex-wrap gap-4 items-center justify-between">
                  <Button 
                    asChild 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <a 
                      href="https://studio.youtube.com/channel/UCjRjVRJpkKHIrtnBPFS2bfQ" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Visitar Canal Trilhos e Trem
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handleCopy("https://studio.youtube.com/channel/UCjRjVRJpkKHIrtnBPFS2bfQ")}
                    className={copied === "https://studio.youtube.com/channel/UCjRjVRJpkKHIrtnBPFS2bfQ" ? "bg-primary/10" : ""}
                  >
                    <Copy size={16} className="mr-1" /> 
                    {copied === "https://studio.youtube.com/channel/UCjRjVRJpkKHIrtnBPFS2bfQ" ? "Copiado!" : "Copiar Link"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Vídeos em Destaque</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, index) => (
              <Card key={index} className="overflow-hidden border border-border h-full flex flex-col">
                <div className="aspect-video overflow-hidden bg-muted">
                  <img 
                    src={video.thumbnail} 
                    alt={video.titulo}
                    className="w-full h-full object-cover transition-transform hover:scale-105"
                  />
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">{video.titulo}</CardTitle>
                  <CardDescription>{video.descricao}</CardDescription>
                </CardHeader>
                <CardFooter className="flex justify-between mt-auto">
                  <Button 
                    asChild 
                    variant="default"
                    size="sm"
                  >
                    <a 
                      href={video.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                    >
                      <Play size={16} className="mr-1" /> Assistir
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleCopy(video.link)}
                    className={copied === video.link ? "bg-primary/10" : ""}
                  >
                    <Share2 size={16} className="mr-1" /> 
                    {copied === video.link ? "Copiado!" : "Compartilhar"}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>

        <div className="bg-muted p-6 rounded-lg mb-12">
          <h2 className="text-2xl font-semibold mb-4">Por que nos acompanhar?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Conteúdo Edificante</h3>
              <p className="text-sm text-muted-foreground">Mensagens que fortalecem sua fé e trazem ensinamentos valiosos para o seu dia a dia.</p>
            </div>
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Momentos de Oração</h3>
              <p className="text-sm text-muted-foreground">Espaços dedicados à oração e meditação nas escrituras para fortalecer sua vida espiritual.</p>
            </div>
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Conteúdo Diversificado</h3>
              <p className="text-sm text-muted-foreground">Desde pregações a momentos de louvor, testemunhos e estudos bíblicos para toda a família.</p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Button 
            asChild 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <a 
              href="https://rumble.com/c/c-7697051" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Inscreva-se no Canal Palavra Viva
            </a>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PalavraViva;